import { useState } from "react";
import {
  Search,
  Lock,
  Eye,
  EyeOff,
  Thermometer,
  Boxes,
  ShieldCheck,
  Tag,
  Calendar,
  FolderTree,
} from "lucide-react";
import {
  Input,
  NumberInput,
  TextArea,
  Select,
  SearchableSelect,
  TreeSelect,
  type TreeSelectNode,
  DatePicker,
  Checkbox,
  Radio,
  RadioGroup,
  Switch,
  Slider,
  RangeSlider,
  Badge,
} from "@/shared/ui";

export function TestUiCoreFormInputs() {
  // Input states
  const [textVal, setTextVal] = useState("MO-2026-0318");
  const [searchVal, setSearchVal] = useState("");
  const [qtyVal, setQtyVal] = useState("12500");
  const [showPassword, setShowPassword] = useState(false);

  // DatePicker states
  const [dateVal, setDateVal] = useState("2026-09-30");
  const [dateTimeVal, setDateTimeVal] = useState("2026-09-30T16:00");

  // Select states
  const [selectVal, setSelectVal] = useState("STVQ");
  const [searchableVal, setSearchableVal] = useState("SP-CT-170220");
  const [treeVal, setTreeVal] = useState("TC");
  const [orgTreeVal, setOrgTreeVal] = useState("LAB");

  // Selection controls
  const [check1, setCheck1] = useState(true);
  const [check2, setCheck2] = useState(false);
  const [radioVal, setRadioVal] = useState("Ưu tiên cao");
  const [switch1, setSwitch1] = useState(true);
  const [switch2, setSwitch2] = useState(false);
  const [switchSm, setSwitchSm] = useState(true);
  const [switchLg, setSwitchLg] = useState(true);
  const [sliderVal, setSliderVal] = useState(10);
  const [rangeVal, setRangeVal] = useState<[number, number]>([8, 15]);

  const plantOptions = [
    { value: "STVQ", label: "NM Thủy sản Sóc Trăng (STVQ)" },
    { value: "CT01", label: "Kho Lạnh Tổng Cần Thơ (CT-01)" },
    { value: "VL02", label: "Xưởng Chế biến Vĩnh Long (VL-02)" },
    { value: "BL01", label: "Trại nuôi Tôm Bạc Liêu (BL-01)" },
  ];

  const productOptions = [
    { value: "SP-CT-170220", label: "Cá tra fillet đông rời IQF size 170-220g (Thùng 10kg)" },
    { value: "SP-CT-220UP", label: "Cá tra fillet đông rời IQF size >220g (Thùng 10kg)" },
    { value: "SP-TS-HOSO", label: "Tôm sú nguyên con đông lạnh semi-IQF (15 con/kg)" },
    { value: "SP-MC-TUBE", label: "Mực nang ống làm sạch đóng khay 500g" },
  ];

  const priorityOptions = [
    { value: "Bình thường", label: "Bình thường (Tiêu chuẩn SLA 72h)" },
    { value: "Ưu tiên cao", label: "Ưu tiên cao (Xử lý trong 48h)" },
    { value: "Khẩn cấp", label: "Khẩn cấp (SLA xử lý trong 24h)" },
  ];

  const categoryTreeNodes: TreeSelectNode[] = [
    { value: "PKG", label: "Bao bì", code: "PKG" },
    { value: "SFG", label: "Bán thành phẩm", code: "SFG" },
    {
      value: "RM",
      label: "Nguyên liệu",
      code: "RM",
      children: [
        { value: "TC", label: "Trái cây tươi", code: "TC" },
        { value: "THS", label: "Thủy hải sản tươi sống", code: "THS" },
        { value: "GV", label: "Gia vị & Phụ gia chế biến", code: "GV" },
      ],
    },
    { value: "REWORK", label: "Rework", code: "REWORK" },
    {
      value: "FG",
      label: "Thành phẩm",
      code: "FG",
      children: [
        { value: "FG-EU", label: "Thành phẩm xuất khẩu EU", code: "FG-EU" },
        { value: "FG-VN", label: "Thành phẩm nội địa", code: "FG-VN" },
      ],
    },
  ];

  const orgTreeNodes: TreeSelectNode[] = [
    {
      value: "BGD",
      label: "Ban Giám Đốc",
      code: "BGD",
      children: [
        {
          value: "KSX",
          label: "Khối Sản Xuất",
          code: "KSX",
          children: [
            { value: "CB1", label: "Xưởng Chế biến 1", code: "CB1" },
            { value: "CDG", label: "Xưởng Cấp đông & Đóng gói", code: "CDG" },
          ],
        },
        {
          value: "KQC",
          label: "Khối Quản lý Chất lượng QC",
          code: "KQC",
          children: [
            { value: "LAB", label: "Phòng Lab Kiểm nghiệm vi sinh", code: "LAB" },
            { value: "HACCP", label: "Tổ Giám sát HACCP", code: "HACCP" },
          ],
        },
        {
          value: "SCM",
          label: "Khối Chuỗi Cung ứng",
          code: "SCM",
          children: [
            { value: "PUR", label: "Bộ phận Mua hàng Kho vận", code: "PUR" },
          ],
        },
      ],
    },
  ];

  return (
    <div className="space-y-6">
      {/* 1. DatePicker & DateTimePicker Showcase */}
      <div className="rounded-control border border-border bg-surface p-4 shadow-xs space-y-4">
        <div className="flex items-center justify-between border-b border-border/60 pb-2.5">
          <div className="flex items-center gap-2">
            <div className="flex size-7 items-center justify-center rounded-md bg-primary-soft text-primary font-bold">
              <Calendar className="size-4" />
            </div>
            <div>
              <h3 className="m-0 text-body font-bold text-text">
                1. DatePicker & DateTimePicker (Bộ Chọn Thời Gian Chuẩn ERP)
              </h3>
              <p className="m-0 text-caption text-text-muted">
                Lịch tiếng Việt, chuẩn độ cao 36px, hỗ trợ chọn ngày hoặc kèm giờ phút, phím tắt Hôm nay / Xóa
              </p>
            </div>
          </div>
          <Badge tone="success" label="Mới Chuẩn Hóa" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pt-1">
          {/* Date Only */}
          <div className="space-y-1.5 rounded-md border border-border/70 bg-bg/20 p-3">
            <span className="text-[11px] font-semibold text-text flex items-center justify-between">
              <span>Chọn Ngày (mode="date")</span>
              <span className="font-mono text-primary font-bold">{dateVal || "(Trống)"}</span>
            </span>
            <DatePicker
              mode="date"
              value={dateVal}
              onChange={(val) => setDateVal(val)}
              placeholder="Chọn ngày chứng từ..."
              className="h-[36px] min-h-[36px]"
            />
            <p className="text-[10.5px] text-text-muted m-0">Hiển thị chuẩn: DD/MM/YYYY</p>
          </div>

          {/* DateTime Mode */}
          <div className="space-y-1.5 rounded-md border border-border/70 bg-bg/20 p-3">
            <span className="text-[11px] font-semibold text-text flex items-center justify-between">
              <span>Chọn Ngày & Giờ (mode="datetime")</span>
              <span className="font-mono text-primary font-bold">{dateTimeVal || "(Trống)"}</span>
            </span>
            <DatePicker
              mode="datetime"
              value={dateTimeVal}
              onChange={(val) => setDateTimeVal(val)}
              placeholder="Chọn ngày & giờ xuất xưởng..."
              className="h-[36px] min-h-[36px]"
            />
            <p className="text-[10.5px] text-text-muted m-0">Hiển thị chuẩn: DD/MM/YYYY HH:mm</p>
          </div>

          {/* Disabled & Error variations */}
          <div className="space-y-1.5 rounded-md border border-border/70 bg-bg/20 p-3">
            <span className="text-[11px] font-semibold text-text flex items-center justify-between">
              <span>Trạng thái Có Lỗi (error)</span>
              <Badge tone="danger" label="Validate Failed" />
            </span>
            <DatePicker
              mode="date"
              value="2026-09-01"
              error="Ngày hoàn thành không được nhỏ hơn ngày lập lệnh"
              placeholder="Chọn ngày..."
              className="h-[36px] min-h-[36px]"
            />
          </div>
        </div>
      </div>

      {/* 2. Text Input & Variations */}
      <div className="rounded-control border border-border bg-surface p-4 shadow-xs space-y-4">
        <div className="flex items-center justify-between border-b border-border/60 pb-2.5">
          <div className="flex items-center gap-2">
            <div className="flex size-7 items-center justify-center rounded-md bg-info-soft text-info-fg font-bold">
              <Boxes className="size-4" />
            </div>
            <div>
              <h3 className="m-0 text-body font-bold text-text">
                2. Input Fields (Các Biến Thể Ô Nhập Dữ Liệu)
              </h3>
              <p className="m-0 text-caption text-text-muted">
                Độ cao đồng bộ 36px, font Mono cho mã/số, hỗ trợ icon dẫn đầu (startAdornment) và đơn vị (endAdornment)
              </p>
            </div>
          </div>
          <Badge tone="primary" label="Chuẩn h-36px" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 pt-1">
          {/* Standard Mono Code Input with Required * */}
          <div>
            <Input
              label="Mã định danh"
              required
              value={textVal}
              onChange={(e) => setTextVal(e.target.value)}
              mono
              placeholder="Nhập mã phiếu..."
              className="h-[36px] min-h-[36px] text-[12px]"
            />
          </div>

          {/* Search Input with Start Adornment */}
          <div>
            <Input
              size="sm"
              label="Tìm kiếm với Icon"
              value={searchVal}
              onChange={(e) => setSearchVal(e.target.value)}
              startAdornment={<Search className="size-3.5 text-text-muted" />}
              placeholder="Tìm mã lô, nhà máy..."
              className="h-[36px] min-h-[36px] text-[12px]"
            />
          </div>

          {/* Quantity with Auto-formatted NumberInput, Stepper and Suffix */}
          <div>
            <NumberInput
              label="Số lượng định mức"
              required
              value={qtyVal}
              onValueChange={(val) => setQtyVal(val !== null ? String(val) : "")}
              suffix="kg"
              placeholder="0"
              className="h-[36px] min-h-[36px] text-[12px]"
            />
          </div>

          {/* Temperature with Start & End */}
          <div>
            <Input
              size="sm"
              label="Nhiệt độ cấp đông"
              defaultValue="-18"
              mono
              startAdornment={<Thermometer className="size-3.5 text-info-fg" />}
              endAdornment={<span className="text-[11px] font-bold text-text-muted">°C</span>}
              placeholder="-18"
              className="h-[36px] min-h-[36px] text-[12px]"
            />
          </div>
        </div>

        {/* Second Row: States (Readonly, Disabled, Error, Password) */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 pt-1 border-t border-border/40">
          <div>
            <label className="block text-[11px] font-semibold text-text-muted mb-1">
              Hệ thống tự cấp (Disabled)
            </label>
            <Input
              value="AUTO-LOT-2026-928"
              disabled
              mono
              className="h-[36px] min-h-[36px] text-[12px]"
            />
          </div>

          <div>
            <label className="block text-[11px] font-semibold text-text-muted mb-1">
              Chỉ đọc (Read-only)
            </label>
            <Input
              value="Đã niêm phong KCS"
              readOnly
              className="h-[36px] min-h-[36px] text-[12px]"
            />
          </div>

          <div>
            <label className="block text-[11px] font-semibold text-text-muted mb-1">
              Báo lỗi hợp lệ (Tone error)
            </label>
            <Input
              defaultValue="ABC#"
              error="Ký tự đặc biệt không hợp lệ"
              className="h-[36px] min-h-[36px] text-[12px]"
            />
          </div>

          <div>
            <label className="block text-[11px] font-semibold text-text-muted mb-1">
              Mã bảo mật / Ký điện tử
            </label>
            <div className="relative">
              <Input
                size="sm"
                type={showPassword ? "text" : "password"}
                defaultValue="secretPass2026"
                startAdornment={<Lock className="size-3.5 text-text-muted" />}
                endAdornment={
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="cursor-pointer text-text-muted hover:text-text"
                  >
                    {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                  </button>
                }
                className="h-[36px] min-h-[36px] text-[12px]"
              />
            </div>
          </div>
        </div>

        {/* Multi-line TextArea */}
        <div className="pt-2 border-t border-border/40">
          <label className="block text-[11px] font-semibold text-text-muted mb-1">
            Ghi chú quy trình sản xuất (TextArea)
          </label>
          <TextArea
            rows={2}
            placeholder="Nhập hướng dẫn bảo quản, quy cách đóng gói hoặc ghi chú giám sát CCP..."
            defaultValue="Lô hàng kiểm định HACCP: Yêu cầu rà kim loại 30 phút/lần, lưu mẫu 48h tại phòng kiểm nghiệm."
            className="text-[12px]"
          />
        </div>
      </div>

      {/* 3. Select & SearchableSelect Dropdowns */}
      <div className="rounded-control border border-border bg-surface p-4 shadow-xs space-y-4">
        <div className="flex items-center justify-between border-b border-border/60 pb-2.5">
          <div className="flex items-center gap-2">
            <div className="flex size-7 items-center justify-center rounded-md bg-warning-soft text-warning-fg font-bold">
              <Tag className="size-4" />
            </div>
            <div>
              <h3 className="m-0 text-body font-bold text-text">
                3. Select & SearchableSelect Dropdowns
              </h3>
              <p className="m-0 text-caption text-text-muted">
                Chuẩn hóa tự động đóng dropdown cũ khi mở dropdown mới, lọc tìm kiếm thời gian thực
              </p>
            </div>
          </div>
          <Badge tone="success" label="Mutual Exclusion OK" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-1">
          {/* Standard Select */}
          <div className="space-y-1.5 rounded-md border border-border/70 bg-bg/20 p-3">
            <span className="text-[11px] font-semibold text-text">Select (Radix Select chuẩn)</span>
            <Select
              options={plantOptions}
              value={selectVal}
              onValueChange={(val) => setSelectVal(val)}
              className="h-[36px] min-h-[36px] text-[12px] rounded-md"
            />
            <p className="text-[10.5px] text-text-muted m-0">Lựa chọn nhanh danh sách ngắn không cần search</p>
          </div>

          {/* SearchableSelect */}
          <div className="space-y-1.5 rounded-md border border-border/70 bg-bg/20 p-3">
            <span className="text-[11px] font-semibold text-text flex items-center justify-between">
              <span>SearchableSelect (Lọc trực tiếp)</span>
              <Badge tone="primary" label="Tìm kiếm nhanh" />
            </span>
            <SearchableSelect
              options={productOptions}
              value={searchableVal}
              onValueChange={(val) => setSearchableVal(val)}
              placeholder="Chọn hoặc tìm kiếm mặt hàng..."
              searchPlaceholder="Nhập tên hoặc mã sản phẩm..."
              className="h-[36px] min-h-[36px] text-[12px] rounded-md"
            />
            <p className="text-[10.5px] text-text-muted m-0">Tối ưu cho danh mục hàng nghìn sản phẩm, kho bãi</p>
          </div>
        </div>
      </div>

      {/* 3.1 TreeSelect Showcase (Selectable Treeview) */}
      <div className="rounded-control border border-border bg-surface p-4 shadow-xs space-y-4">
        <div className="flex items-center justify-between border-b border-border/60 pb-2.5">
          <div className="flex items-center gap-2">
            <div className="flex size-7 items-center justify-center rounded-md bg-primary-soft text-primary font-bold">
              <FolderTree className="size-4" />
            </div>
            <div>
              <h3 className="m-0 text-body font-bold text-text">
                3.1 TreeSelect (Selectable Treeview Phân cấp trực quan)
              </h3>
              <p className="m-0 text-caption text-text-muted">
                Bộ chọn dạng cây đa tầng, đường nhánh chỉ dẫn trực quan, tìm kiếm thông minh tự bung nhánh, hỗ trợ mã code & nút thu gọn
              </p>
            </div>
          </div>
          <Badge tone="primary" label="New Core Component" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pt-1">
          {/* Card 1: Category Tree (Mô hình thực tế từ ảnh chụp) */}
          <div className="space-y-1.5 rounded-md border border-border/70 bg-bg/20 p-3 flex flex-col justify-between">
            <div className="space-y-1.5">
              <span className="text-[11px] font-semibold text-text flex items-center justify-between">
                <span>1. Loại vật tư / Nguyên liệu (Chuẩn MDM)</span>
                <span className="font-mono text-[10px] text-text-muted">leaf-only</span>
              </span>
              <TreeSelect
                nodes={categoryTreeNodes}
                value={treeVal}
                onValueChange={(val) => setTreeVal(val)}
                placeholder="Chọn loại"
                searchPlaceholder="Tìm loại"
                emptyText="Không có loại phù hợp"
                size="md"
                clearable
                displayMode="full-path"
                pathSeparator=" > "
              />
            </div>
            <div className="pt-2 border-t border-border/40 text-[10.5px] text-text-muted flex items-center justify-between">
              <span>Đang chọn mã: <code className="font-mono font-semibold text-primary">{treeVal || "(Trống)"}</code></span>
              <span>Đường nối nhánh L chuẩn</span>
            </div>
          </div>

          {/* Card 2: Org Structure Tree (Chọn cả phòng ban cha lẫn con) */}
          <div className="space-y-1.5 rounded-md border border-border/70 bg-bg/20 p-3 flex flex-col justify-between">
            <div className="space-y-1.5">
              <span className="text-[11px] font-semibold text-text flex items-center justify-between">
                <span>2. Cơ cấu tổ chức / Phòng ban</span>
                <span className="font-mono text-[10px] text-primary">selectable-parents</span>
              </span>
              <TreeSelect
                nodes={orgTreeNodes}
                value={orgTreeVal}
                onValueChange={(val) => setOrgTreeVal(val)}
                placeholder="Chọn phòng ban / xưởng…"
                searchPlaceholder="Tìm khối, phòng ban…"
                size="md"
                selectableParents={true}
                showFolderIcons={true}
                showExpandCollapse={true}
                displayMode="full-path"
                pathSeparator=" > "
              />
            </div>
            <div className="pt-2 border-t border-border/40 text-[10.5px] text-text-muted flex items-center justify-between">
              <span>Đang chọn mã: <code className="font-mono font-semibold text-primary">{orgTreeVal || "(Trống)"}</code></span>
              <span>Icon thư mục & mở rộng tất cả</span>
            </div>
          </div>

          {/* Card 3: Validation States (Lỗi & Vô hiệu hóa) */}
          <div className="space-y-2 rounded-md border border-border/70 bg-bg/20 p-3 flex flex-col justify-between">
            <div className="space-y-2">
              <span className="text-[11px] font-semibold text-text">
                3. Trạng thái Bắt buộc / Báo lỗi & Disabled
              </span>
              <TreeSelect
                nodes={categoryTreeNodes}
                value=""
                placeholder="Vui lòng chọn loại danh mục…"
                error="Trường này là bắt buộc, không được để trống"
                size="sm"
              />
              <TreeSelect
                nodes={categoryTreeNodes}
                value="PKG"
                disabled
                size="sm"
              />
            </div>
            <p className="text-[10.5px] text-text-muted m-0 pt-1 border-t border-border/40">
              Kiểm soát kích cỡ sm/md/lg và phản hồi lỗi trực quan
            </p>
          </div>
        </div>
      </div>

      {/* 4. Selection Controls (Checkbox, Radio, Switch, Slider) */}
      <div className="rounded-control border border-border bg-surface p-4 shadow-xs space-y-4">
        <div className="flex items-center justify-between border-b border-border/60 pb-2.5">
          <div className="flex items-center gap-2">
            <div className="flex size-7 items-center justify-center rounded-md bg-accent/20 text-accent font-bold">
              <ShieldCheck className="size-4" />
            </div>
            <div>
              <h3 className="m-0 text-body font-bold text-text">
                4. Selection Controls (Checkbox, Radio, Switch, Slider)
              </h3>
              <p className="m-0 text-caption text-text-muted">
                Các bộ điều khiển chọn lựa nhị phân, nhóm tùy chọn và thanh trượt định mức
              </p>
            </div>
          </div>
          <Badge tone="neutral" label="Touch Friendly" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 pt-1">
          {/* Checkboxes */}
          <div className="space-y-2 rounded-md border border-border/70 bg-bg/20 p-3">
            <span className="text-[11px] font-bold text-text uppercase tracking-wider block">Checkbox</span>
            <Checkbox
              checked={check1}
              onCheckedChange={(val) => setCheck1(Boolean(val))}
              label="Đạt chuẩn kiểm dịch ATTP"
              description="Chứng thư kiểm dịch cấp bởi Chi cục QLCL"
            />
            <Checkbox
              checked={check2}
              onCheckedChange={(val) => setCheck2(Boolean(val))}
              label="Kiểm tra kim loại CCP-2"
              description="Đã quét qua máy rà kim loại"
            />
            <Checkbox
              checked={false}
              disabled
              label="Vô hiệu hóa (Locked)"
              description="Không thể chỉnh sửa"
            />
          </div>

          {/* Radio Group */}
          <div className="space-y-2 rounded-md border border-border/70 bg-bg/20 p-3">
            <span className="text-[11px] font-bold text-text uppercase tracking-wider block">Radio Group</span>
            <RadioGroup
              value={radioVal}
              onValueChange={setRadioVal}
              options={priorityOptions}
            />
          </div>

          {/* Switches with Sizes */}
          <div className="space-y-3 rounded-md border border-border/70 bg-bg/20 p-3">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-bold text-text uppercase tracking-wider block">
                Switch Toggle (Small · Medium · Large)
              </span>
              <span className="text-[10px] font-mono text-primary font-semibold">Radix + 3 Sizes</span>
            </div>

            {/* Medium (Default) */}
            <Switch
              size="md"
              checked={switch1}
              onCheckedChange={setSwitch1}
              label="Cảnh báo nhiệt độ tự động (Medium)"
              description="Gửi tin nhắn khi hầm đông vượt -18°C"
            />

            {/* Small */}
            <Switch
              size="sm"
              checked={switchSm}
              onCheckedChange={setSwitchSm}
              label="Tự động đồng bộ WMS (Small)"
              description="Kích thước nhỏ gọn 32x18px cho bảng dữ liệu"
            />

            {/* Large */}
            <Switch
              size="lg"
              checked={switchLg}
              onCheckedChange={setSwitchLg}
              label="Kích hoạt chế độ kiểm toán (Large)"
              description="Kích thước lớn 48x26px dễ bấm trên cảm ứng"
            />

            {/* Disabled */}
            <Switch
              size="md"
              checked={false}
              disabled
              label="Khóa cài đặt hệ thống (Disabled)"
              description="Quyền hạn quản trị viên cấp cao"
            />
          </div>

          {/* Slider & RangeSlider */}
          <div className="space-y-4 rounded-md border border-border/70 bg-bg/20 p-3">
            {/* 1. Slider đơn (1 đầu) */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold text-text uppercase tracking-wider">Độ mạ băng (1 đầu)</span>
                <span className="font-mono text-primary font-bold text-[12px]">{sliderVal}%</span>
              </div>
              <p className="text-[10.5px] text-text-muted m-0">Định mức mạ băng chuẩn cho cá tra xuất khẩu</p>
              <Slider
                value={[sliderVal]}
                onValueChange={(vals) => setSliderVal(vals[0] ?? 10)}
                min={0}
                max={25}
                step={1}
              />
              <div className="flex justify-between text-[10px] font-mono text-text-muted">
                <span>0% (Không mạ)</span>
                <span>10% (Chuẩn)</span>
                <span>25% (Tối đa)</span>
              </div>
            </div>

            {/* 2. Range Slider (Kéo 2 đầu) */}
            <div className="pt-3 border-t border-border/60 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold text-text uppercase tracking-wider">Dải mạ băng (Kéo 2 đầu)</span>
                <span className="font-mono text-primary font-bold text-[12px]">{rangeVal[0]}% ~ {rangeVal[1]}%</span>
              </div>
              <p className="text-[10.5px] text-text-muted m-0">Khoảng sai số cho phép khi xuất xưởng (Min - Max)</p>
              <RangeSlider
                value={rangeVal}
                onValueChange={setRangeVal}
                min={0}
                max={25}
                step={1}
                minStepsBetweenThumbs={1}
              />
              <div className="flex justify-between text-[10px] font-mono text-text-muted">
                <span>0% (Min)</span>
                <span>Biên độ {rangeVal[1] - rangeVal[0]}%</span>
                <span>25% (Max)</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
